import { useState, useCallback, useMemo, useEffect } from 'react';
import { createUniverseShare } from '../api/authClient';
import { API_BASE } from '../api/config';
import '../styles/shareGalaxy.css';

function ShareUniverseDrawer({ onClose, canvasRef, universeLabel, universeData, overrideNodes, overrideLinks }) {
  const [mapName, setMapName] = useState(universeLabel || 'My Universe');
  const [sharing, setSharing] = useState(false);
  const [thumbnailDataUrl, setThumbnailDataUrl] = useState(null);
  const [error, setError] = useState(null);

  const nodes = useMemo(() => overrideNodes || [], [overrideNodes]);
  const links = useMemo(() => overrideLinks || [], [overrideLinks]);
  const recCount = useMemo(() => nodes.filter((n) => n.isRecommendation).length, [nodes]);
  const artistCount = useMemo(() => nodes.length - recCount, [nodes.length, recCount]);

  // Capture thumbnail on mount
  useEffect(() => {
    const dataUrl = canvasRef.current?.captureImage({ watermark: true });
    if (dataUrl) setThumbnailDataUrl(dataUrl);
  }, [canvasRef]);

  // Compress canvas to a smaller JPEG thumbnail for OG images
  const compressThumbnail = useCallback(() => {
    const canvas = canvasRef.current?.captureImage ? canvasRef.current : null;
    if (!canvas) return null;
    const sourceCanvas = document.querySelector('canvas');
    if (!sourceCanvas) return null;
    try {
      const maxW = 1200;
      const maxH = 630;
      const scale = Math.min(maxW / sourceCanvas.width, maxH / sourceCanvas.height, 1);
      const offscreen = document.createElement('canvas');
      offscreen.width = Math.round(sourceCanvas.width * scale);
      offscreen.height = Math.round(sourceCanvas.height * scale);
      const ctx = offscreen.getContext('2d');
      ctx.drawImage(sourceCanvas, 0, 0, offscreen.width, offscreen.height);
      // Add watermark
      ctx.font = "600 14px 'Space Grotesk', sans-serif";
      ctx.fillStyle = 'rgba(255, 255, 255, 0.25)';
      ctx.textAlign = 'right';
      ctx.fillText('Stellar', offscreen.width - 16, offscreen.height - 12);
      return offscreen.toDataURL('image/jpeg', 0.7);
    } catch {
      return null;
    }
  }, [canvasRef]);

  // Create share link and open native share
  const handleShare = useCallback(async () => {
    if (nodes.length === 0) return;
    setSharing(true);
    setError(null);
    try {
      const compressedThumb = compressThumbnail();

      const res = await createUniverseShare({
        mapName: mapName.trim() || 'My Universe',
        universeData,
        nodeCount: nodes.length,
        linkCount: links.length,
        thumbnail: compressedThumb,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Share failed');

      // In the Capacitor iOS app window.location.origin is capacitor://localhost,
      // which navigator.share rejects (non-http URL) and no one else can open —
      // use the public origin baked into API_BASE instead.
      const shareBase = API_BASE || window.location.origin;
      const fullUrl = `${shareBase}/universe/${data.id}`;
      const statsText = `${artistCount} artists · ${recCount} recs · ${links.length} connections`;

      if (navigator.share) {
        try {
          await navigator.share({ title: mapName, text: statsText, url: fullUrl });
        } catch {}
      } else {
        try {
          await navigator.clipboard.writeText(fullUrl);
        } catch {}
      }
    } catch (err) {
      console.warn('Share failed:', err.message);
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSharing(false);
    }
  }, [nodes, links, mapName, universeData, compressThumbnail, artistCount, recCount]);

  return (
    <div className="share-galaxy-drawer">
      <button className="share-galaxy-drawer-close" onClick={onClose}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>

      <div className="share-galaxy-drawer-content">
        {/* Header */}
        <div className="share-galaxy-header">
          <h3 className="share-galaxy-title">Share My Universe</h3>
          <input
            className="share-galaxy-name-input"
            type="text"
            value={mapName}
            onChange={(e) => setMapName(e.target.value)}
            maxLength={80}
            placeholder="Universe name"
          />
          <div className="share-galaxy-meta">
            {artistCount} artist{artistCount !== 1 ? 's' : ''} &middot; {recCount} rec{recCount !== 1 ? 's' : ''} &middot; {links.length} connection{links.length !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Preview thumbnail */}
        {thumbnailDataUrl && (
          <div className="share-galaxy-preview">
            <img src={thumbnailDataUrl} alt="Universe preview" />
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="share-galaxy-error">{error}</div>
        )}

        {/* Actions */}
        <div className="share-galaxy-actions">
          <button
            className="share-galaxy-action-btn primary"
            onClick={handleShare}
            disabled={sharing || nodes.length === 0}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
              <circle cx="18" cy="5" r="3" />
              <circle cx="6" cy="12" r="3" />
              <circle cx="18" cy="19" r="3" />
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
            </svg>
            {sharing ? 'Creating link...' : 'Share'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ShareUniverseDrawer;
